import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type PaletteMode = 'light' | 'dark';

interface ThemeContextType {
  mode: PaletteMode;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const TimeLynxxThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize theme mode from localStorage or default to 'light'
  const [mode, setMode] = useState<PaletteMode>(() => {
    const savedMode = localStorage.getItem('timelynxx-theme-mode');
    return (savedMode as PaletteMode) || 'light';
  });

  // Save theme mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('timelynxx-theme-mode', mode);
  }, [mode]);

  const toggleMode = () => {
    setMode((prevMode: PaletteMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const value: ThemeContextType = {
    mode,
    toggleMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTimeLynxxTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTimeLynxxTheme must be used within a TimeLynxxThemeProvider');
  }
  return context;
};